import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, FileText, Pencil, Plus, Search, Upload } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Checkbox, Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { EmptyState, Pagination, TBody, TD, TH, THead, TR, Table } from '@/components/ui/Table';
import { SectionSpinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/Toast';
import { Can } from '@/app/guards/RoleGuard';
import { invoicesApi } from '@/services/endpoints/transactions';
import { queryKeys } from '@/services/queryKeys';
import { ApiError } from '@/services/apiClient';
import { formatCents } from '@/domain/money';
import { canCancelInvoice, canEditInvoice, STATUS_TONE } from '@/domain/invoiceStatus';
import { formatDate } from '@/lib/dateUtils';
import { InvoiceFormModal } from '../components/InvoiceFormModal';
import { InvoiceImportModal } from '../components/InvoiceImportModal';
import type { Invoice, InvoiceStatus } from '@/domain/types';

export function InvoicesPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);

  const [editing, setEditing] = useState<Invoice | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [isImportOpen, setImportOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<Invoice | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const params = {
    q: search || undefined,
    page,
    pageSize: 25,
    outstandingOnly,
    overdueOnly,
    sortBy: 'dueDate' as const,
    sortDir: 'asc' as const,
  };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.invoices.list(params),
    queryFn: () => invoicesApi.list(params),
    placeholderData: (previous) => previous,
  });

  const cancelInvoice = useMutation({
    mutationFn: () => invoicesApi.cancel(cancelTarget!.id, cancelReason.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      toast.success(t('invoice.cancelled'));
      setCancelTarget(null);
      setCancelReason('');
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : t('errors.unknown')),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('invoice.title')}
        actions={
          <Can permission="invoices:create">
            <>
              <Button
                variant="outline"
                onClick={() => setImportOpen(true)}
                leftIcon={<Upload className="h-4 w-4" />}
              >
                {t('common.import')}
              </Button>
              <Button
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                {t('invoice.new')}
              </Button>
            </>
          </Can>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-4 border-b border-slate-200 p-4">
          <div className="relative min-w-56 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder={t('common.searchPlaceholder')}
              className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>

          <Checkbox
            label={t('invoice.outstandingOnly')}
            checked={outstandingOnly}
            onChange={(event) => {
              setOutstandingOnly(event.target.checked);
              setPage(1);
            }}
          />

          <Checkbox
            label={t('invoice.overdueOnly')}
            checked={overdueOnly}
            onChange={(event) => {
              setOverdueOnly(event.target.checked);
              setPage(1);
            }}
          />
        </div>

        {isLoading ? (
          <SectionSpinner />
        ) : !data || data.rows.length === 0 ? (
          <EmptyState icon={<FileText className="h-6 w-6" />} title={t('invoice.empty')} />
        ) : (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>{t('invoice.invoiceNo')}</TH>
                  <TH>{t('invoice.customer')}</TH>
                  <TH>{t('shop.agent')}</TH>
                  <TH align="right">{t('invoice.amount')}</TH>
                  <TH align="right">{t('invoice.outstanding')}</TH>
                  <TH>{t('invoice.dueDate')}</TH>
                  <TH align="center">{t('common.status')}</TH>
                  <TH align="right">{t('common.actions')}</TH>
                </TR>
              </THead>

              <TBody>
                {data.rows.map((invoice) => (
                  <TR key={invoice.id}>
                    <TD className="font-medium text-slate-900">{invoice.invoiceNo}</TD>
                    <TD>{invoice.shopName}</TD>
                    <TD className="text-slate-500">{invoice.agentName ?? '—'}</TD>
                    <TD align="right" numeric>
                      {formatCents(invoice.amountCents)}
                    </TD>
                    <TD align="right" numeric>
                      {invoice.outstandingCents > 0 ? formatCents(invoice.outstandingCents) : '—'}
                    </TD>
                    <TD>
                      <span className={invoice.isOverdue ? 'font-medium text-red-600' : undefined}>
                        {formatDate(invoice.dueDate)}
                      </span>
                    </TD>
                    <TD align="center">
                      <Badge tone={STATUS_TONE[invoice.status as InvoiceStatus]}>
                        {t(`invoice.status.${invoice.status}` as never)}
                      </Badge>
                    </TD>
                    <TD align="right">
                      <Can permission="invoices:update">
                        <div className="flex justify-end gap-1">
                          {canEditInvoice(invoice.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditing(invoice);
                                setFormOpen(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canCancelInvoice(invoice.status, invoice.allocatedCents) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCancelTarget(invoice)}
                              title={t('invoice.cancelTitle')}
                            >
                              <Ban className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </Can>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>

            <Pagination meta={data.meta} onPageChange={setPage} />
          </>
        )}
      </Card>

      <InvoiceFormModal isOpen={isFormOpen} onClose={() => setFormOpen(false)} invoice={editing} />
      <InvoiceImportModal isOpen={isImportOpen} onClose={() => setImportOpen(false)} />

      <Modal
        isOpen={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        title={t('invoice.cancelTitle')}
        description={cancelTarget?.invoiceNo}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelTarget(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => cancelInvoice.mutate()}
              isLoading={cancelInvoice.isPending}
              disabled={cancelReason.trim().length < 3}
            >
              {t('common.confirm')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {t('invoice.cancelWarning')}
          </p>
          <Input
            label={t('invoice.cancelReason')}
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            required
            autoFocus
          />
        </div>
      </Modal>
    </div>
  );
}
